'use client';

import Container from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { PostCard } from '@/components/ui/post-card';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import type { Document } from '@contentful/rich-text-types';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { ArrowRight } from 'lucide-react';
import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import styles from './insights-section.module.scss';

const CONTENT_TYPE_MAP: Record<string, string> = {
  showBlog: 'blog',
  showNews: 'news',
  showCaseStudy: 'case-study',
  showMarketingInsights: 'marketing-insights',
  showEvent: 'event',
  showWebinar: 'webinar',
};

type AutoCard = {
  title: string;
  image: string | null;
  imageAlt: string | null;
  tags?: string[];
  description: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  createdAt: string;
};

export type InsightsSectionExperienceProps = {
  children?: React.ReactNode;
  sectionId?: string;
  badge?: string;
  title?: string;

  subHeading?: Document;
  isBackgroundDark?: boolean;
  layoutMode?: 'carousel' | 'grid';
  cardMode?: 'manual' | 'auto';
  showBlog?: boolean;
  showNews?: boolean;
  showCaseStudy?: boolean;
  showMarketingInsights?: boolean;
  showEvent?: boolean;
  showWebinar?: boolean;
  enableAutoscroll?: boolean;
  autoscrollSpeed?: number;
};

export const InsightsSectionExperience = ({
  children,
  sectionId,
  badge,
  title,

  subHeading,
  isBackgroundDark = false,
  layoutMode = 'carousel',
  cardMode = 'manual',
  showBlog,
  showNews,
  showCaseStudy,
  showMarketingInsights,
  showEvent,
  showWebinar,
  enableAutoscroll,
  autoscrollSpeed = 4000,
  className,
  ...props
}: InsightsSectionExperienceProps & HTMLAttributes<HTMLElement>) => {
  const Tag = 'h2' as const;
  const isDark = isBackgroundDark;

  const [autoCards, setAutoCards] = useState<AutoCard[] | null>(null);

  const toggles = {
    showBlog,
    showNews,
    showCaseStudy,
    showMarketingInsights,
    showEvent,
    showWebinar,
  };

  useEffect(() => {
    if (cardMode !== 'auto') {
      setAutoCards(null);
      return;
    }

    const types = Object.entries(toggles)
      .filter(([, enabled]) => enabled)
      .map(([key]) => CONTENT_TYPE_MAP[key])
      .filter(Boolean)
      .join(',');

    if (!types) {
      setAutoCards(null);
      return;
    }

    const limit = layoutMode === 'grid' ? 4 : 20;

    fetch(`/api/insights?types=${types}&limit=${limit}&preview=true`)
      .then((res) => res.json())
      .then((json: AutoCard[]) => setAutoCards(json))
      .catch(() => setAutoCards(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cardMode,
    showBlog,
    showNews,
    showCaseStudy,
    showMarketingInsights,
    showEvent,
    showWebinar,
    layoutMode,
  ]);

  const manualChildren = React.Children.toArray(children);
  const isManual = cardMode === 'manual';
  const slideCount = isManual ? manualChildren.length : (autoCards?.length ?? 0);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressKey, setProgressKey] = useState(0);
  const [progressActive, setProgressActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickStartRef = useRef<number>(0);
  const pauseElapsedRef = useRef<number>(0);

  const shouldAutoscroll = layoutMode === 'carousel' && !!enableAutoscroll;

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    layoutMode === 'carousel'
      ? {
          initial: 0,
          loop: true,
          slides: {
            perView: 1.15,
            spacing: 20,
          },
          breakpoints: {
            '(min-width: 768px)': {
              slides: { perView: 2.15, spacing: 20 },
            },
            '(min-width: 1024px)': {
              slides: { perView: 3.15, spacing: 20 },
            },
            '(min-width: 1280px)': {
              slides: { perView: 3.5, spacing: 20 },
            },
          },
          slideChanged(slider) {
            if (!slider.track.details) return;
            setCurrentSlide(slider.track.details.rel);
            setProgressKey((k) => k + 1);
          },
        }
      : false,
  );

  // Force keen-slider to recalculate after layout settles
  useEffect(() => {
    if (layoutMode !== 'carousel' || !instanceRef.current) return;

    // Delayed update to handle late layout shifts (Studio re-renders)
    const timer = setTimeout(() => instanceRef.current?.update(), 100);

    // Also update on resize in case container width changes
    const onResize = () => instanceRef.current?.update();
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [layoutMode, instanceRef, slideCount]);

  const scheduleNext = (delay?: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    tickStartRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      if (instanceRef.current?.track.details) {
        instanceRef.current.next();
      }
      scheduleNext();
    }, delay ?? autoscrollSpeed);
  };

  const restartTimer = () => {
    if (!shouldAutoscroll) return;
    scheduleNext();
  };

  useEffect(() => {
    if (!shouldAutoscroll) return;
    setProgressActive(true);
    scheduleNext();
    return () => {
      setProgressActive(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoscroll, autoscrollSpeed, instanceRef]);

  const handleMouseEnter = () => {
    if (!shouldAutoscroll) return;
    pauseElapsedRef.current = Date.now() - tickStartRef.current;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (!shouldAutoscroll) return;
    setIsHovered(false);
    const remaining = Math.max(0, autoscrollSpeed - pauseElapsedRef.current);
    scheduleNext(remaining);
  };

  const goToSlide = (index: number) => {
    instanceRef.current?.moveToIdx(index);
    restartTimer();
  };

  const goPrev = () => {
    const slider = instanceRef.current;
    if (!slider?.track.details) return;
    slider.moveToIdx(slider.track.details.abs - 1);
    restartTimer();
  };

  const goNext = () => {
    const slider = instanceRef.current;
    if (!slider?.track.details) return;
    slider.moveToIdx(slider.track.details.abs + 1);
    restartTimer();
  };

  const hasHeader = !!(badge || title || subHeading?.content);

  const renderSlides = () => {
    if (isManual) {
      return manualChildren.map((child, i) => (
        <div key={i} className={cn('keen-slider__slide card-shadow', styles.slide)}>
          {child}
        </div>
      ));
    }
    return autoCards?.map((card, i) => (
      <div
        key={`${card.title}-${i}`}
        className={cn('keen-slider__slide card-shadow', styles.slide)}
      >
        <PostCard
          image={card.image}
          imageAlt={card.imageAlt}
          tags={card.tags}
          title={card.title}
          description={card.description}
          date={card.createdAt}
          ctaLabel={card.ctaLabel}
          ctaUrl={card.ctaUrl}
          className={cn('text-left', styles.carousel__card)}
        />
      </div>
    ));
  };

  const renderGrid = () => {
    if (isManual) return manualChildren.slice(0, 4);
    return autoCards
      ?.slice(0, 4)
      .map((card, i) => (
        <PostCard
          key={`${card.title}-${i}`}
          image={card.image}
          imageAlt={card.imageAlt}
          tags={card.tags}
          title={card.title}
          description={card.description}
          date={card.createdAt}
          ctaLabel={card.ctaLabel}
          ctaUrl={card.ctaUrl}
          className="text-left"
        />
      ));
  };

  return (
    <section
      id={sectionId}
      className={cn(
        styles.section,
        styles['section--no-vertical-padding'],
        isDark && styles['section--dark'],
        className,
      )}
      {...props}
    >
      <div className="relative z-10 flex flex-col gap-12">
        {(hasHeader || (layoutMode === 'carousel' && slideCount > 1)) && (
          <Container>
            <div className={styles.header}>
              {hasHeader && (
                <div className={styles.header__left}>
                  {badge && <Badge text={badge} />}
                  {title && (
                    <div
                      className={cn(
                        styles.header__heading,
                        isDark ? styles['header__heading--dark'] : styles['header__heading--light'],
                      )}
                    >
                      <Tag>{title}</Tag>
                    </div>
                  )}
                  {subHeading?.content && (
                    <div
                      className={cn(
                        styles.header__subheading,
                        isDark
                          ? styles['header__subheading--dark']
                          : styles['header__subheading--light'],
                        'text-left',
                      )}
                    >
                      <RichTextViewer content={subHeading} />
                    </div>
                  )}
                </div>
              )}

              {layoutMode === 'carousel' && slideCount > 1 && (
                <div className={styles.header__nav}>
                  <div className={styles.nav__dots}>
                    {Array.from({ length: slideCount }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => goToSlide(i)}
                        className={cn(
                          styles.nav__dot,
                          i === currentSlide && styles['nav__dot--active'],
                          i === currentSlide && progressActive && styles['nav__dot--autoscroll'],
                          isDark && styles['nav__dot--dark'],
                        )}
                        aria-label={`Go to slide ${i + 1}`}
                      >
                        {progressActive && i === currentSlide && (
                          <span
                            key={progressKey}
                            className={styles.nav__progress}
                            style={
                              {
                                '--progress-duration': `${autoscrollSpeed}ms`,
                                animationPlayState: isHovered ? 'paused' : 'running',
                              } as React.CSSProperties
                            }
                          />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className={styles.nav__arrows}>
                    <button
                      onClick={goPrev}
                      className={styles.nav__arrow}
                      aria-label="Previous slide"
                    >
                      <ArrowRight width={24} height={24} className={styles.nav__arrow_prev} />
                    </button>
                    <button onClick={goNext} className={styles.nav__arrow} aria-label="Next slide">
                      <ArrowRight width={24} height={24} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Container>
        )}

        {layoutMode === 'grid' ? (
          <Container>
            <div className={cn(styles.cards_grid, slideCount === 3 && styles['cards_grid--three'])}>
              {renderGrid()}
            </div>
          </Container>
        ) : (
          <div
            className={styles.carousel}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              ref={sliderRef}
              className={cn('keen-slider', styles.slider, styles.carousel__track)}
            >
              {renderSlides()}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
